/**
 * useAuth Hook
 * =============
 * React Query hooks for authentication operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/stores/authStore';
import * as authApi from '@/api/auth';
import type { User } from '@/types';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
  apiKeys: () => [...authKeys.all, 'api-keys'] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Get current user info.
 */
export function useCurrentUser() {
  const { isAuthenticated, accessToken } = useAuthStore();

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: authApi.getCurrentUser,
    enabled: isAuthenticated && !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Get API keys.
 */
export function useAPIKeys() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: authKeys.apiKeys(),
    queryFn: authApi.listAPIKeys,
    enabled: isAuthenticated,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Login mutation.
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (data: authApi.LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      setAuth(response.access_token, response.refresh_token, response.user);
      queryClient.setQueryData(authKeys.me(), response.user);
      toast.success(`Welcome back, ${response.user.first_name}!`);
      navigate('/dashboard');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Login failed';
      toast.error(message);
    },
  });
}

/**
 * Register mutation.
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: authApi.RegisterRequest) => authApi.register(data),
    onSuccess: (response) => {
      toast.success('Registration successful! Please log in.');
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Registration failed';
      toast.error(message);
    },
  });
}

/**
 * SSO exchange mutation.
 */
export function useSSOExchange() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();

  return useMutation({
    mutationFn: (data: authApi.SSOExchangeRequest) => authApi.exchangeSSO(data),
    onSuccess: (response) => {
      storeLogin(response.access_token, response.refresh_token, response.user);
      queryClient.setQueryData(authKeys.me(), response.user);
      
      // Navigate based on whether there's a patient context
      if (response.patient_id) {
        navigate(`/patients/${response.patient_id}`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'SSO authentication failed';
      toast.error(message);
      navigate('/login');
    },
  });
}

/**
 * Logout mutation.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout: storeLogout } = useAuthStore();

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      storeLogout();
      queryClient.clear();
      navigate('/login');
    },
    onError: () => {
      // Even if server logout fails, clear local state
      storeLogout();
      queryClient.clear();
      navigate('/login');
    },
  });
}

/**
 * Change password mutation.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: authApi.PasswordChangeRequest) => authApi.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to change password';
      toast.error(message);
    },
  });
}

/**
 * Request password reset mutation.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
    onSuccess: () => {
      toast.success('If an account exists with that email, you will receive a reset link.');
    },
    onError: () => {
      // Don't reveal if email exists or not
      toast.success('If an account exists with that email, you will receive a reset link.');
    },
  });
}

/**
 * Confirm password reset mutation.
 */
export function useConfirmPasswordReset() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      authApi.confirmPasswordReset(token, newPassword),
    onSuccess: () => {
      toast.success('Password reset successful! Please log in.');
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to reset password';
      toast.error(message);
    },
  });
}

/**
 * Create API key mutation.
 */
export function useCreateAPIKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: authApi.APIKeyCreateRequest) => authApi.createAPIKey(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: authKeys.apiKeys() });
      toast.success('API key created. Make sure to copy it - you won\'t see it again!');
      return response;
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to create API key';
      toast.error(message);
    },
  });
}

/**
 * Revoke API key mutation.
 */
export function useRevokeAPIKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => authApi.revokeAPIKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.apiKeys() });
      toast.success('API key revoked');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to revoke API key';
      toast.error(message);
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Check if user has a specific role.
 */
export function useHasRole(role: string | string[]): boolean {
  const { user } = useAuthStore();
  if (!user) return false;
  
  const roles = Array.isArray(role) ? role : [role];
  return roles.includes(user.role);
}

/**
 * Check if user has a specific permission.
 */
export function useHasPermission(permission: string): boolean {
  const { user } = useAuthStore();
  if (!user) return false;
  
  // Map roles to permissions
  const rolePermissions: Record<string, string[]> = {
    system_admin: ['*'],
    tenant_admin: [
      'manage_users',
      'manage_settings',
      'view_audit_logs',
      'manage_api_keys',
      'create_charts',
      'finalize_charts',
      'view_patients',
      'manage_patients',
    ],
    provider: [
      'create_charts',
      'finalize_charts',
      'view_patients',
      'manage_patients',
    ],
    hygienist: [
      'create_charts',
      'view_patients',
    ],
    front_desk: [
      'view_patients',
      'manage_patients',
    ],
  };
  
  const permissions = rolePermissions[user.role] || [];
  return permissions.includes('*') || permissions.includes(permission);
}

/**
 * Get user's display name.
 */
export function useUserDisplayName(): string {
  const { user } = useAuthStore();
  if (!user) return '';
  return user.first_name || user.email.split('@')[0];
}

/**
 * Check if user is authenticated.
 */
export function useIsAuthenticated(): boolean {
  return useAuthStore((state) => state.isAuthenticated);
}

/**
 * Get current authenticated user from auth store.
 */
export function useUser() {
  return useAuthStore((state) => state.user);
}
